/**
 * Step 2: Cadastro da Mensagem
 * Header, body, footer e botões
 */

import { useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, ExternalLink, Phone, MessageSquare, CreditCard, Copy, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { MediaUrlInput } from '@/components/ui/media-url-input';
import { MediaFileType } from '@/types/media';
import { HeaderFormat, TemplateButton, HEADER_FORMATS, BUTTON_TYPES, MAX_BUTTONS, MAX_BUTTON_TEXT_LENGTH } from './types';
// Sanitizador centralizado de emojis — Meta rejeita templates com emoji nos textos de definição
// (header/footer/botões/valores de exemplo). O CORPO permite emojis (Meta aceita no BODY).
import { stripEmojis, useEmojiSafeHandler } from '@/lib/templates/sanitize-emojis';
// Botão de inserção de emoji (reaproveita EmojiGrid + Dialog existente)
import { EmojiInsertButton } from '@/components/tenant/flows/EmojiInsertButton';

interface TemplateMessageStepProps {
  // Valores
  headerFormat: HeaderFormat;
  headerText: string;
  headerMediaUrl: string;
  bodyText: string;
  footerText: string;
  buttons: TemplateButton[];
  exampleValues?: Record<string, string>;
  
  // Callbacks
  onHeaderFormatChange: (value: HeaderFormat) => void;
  onHeaderTextChange: (value: string) => void;
  onHeaderMediaUrlChange: (value: string) => void;
  onBodyTextChange: (value: string) => void;
  onFooterTextChange: (value: string) => void;
  onButtonsChange: (buttons: TemplateButton[]) => void;
  onExampleValueChange?: (variable: string, value: string) => void;
}

export function TemplateMessageStep({
  headerFormat,
  headerText,
  headerMediaUrl,
  bodyText,
  footerText,
  buttons,
  exampleValues = {},
  onHeaderFormatChange,
  onHeaderTextChange,
  onHeaderMediaUrlChange,
  onBodyTextChange,
  onFooterTextChange,
  onButtonsChange,
  onExampleValueChange,
}: TemplateMessageStepProps) {
  
  // Detecta variáveis no body para exibir campos de exemplo
  const bodyVariables = bodyText.match(/\{\{(\d+)\}\}/g);
  const uniqueBodyVars = bodyVariables ? [...new Set(bodyVariables)].sort() : [];

  // ---------------------------------------------------------------------------
  // Handlers blindados contra emojis (header/footer). Meta rejeita emoji nesses
  // campos. O CORPO permite emojis — usa setter direto + botão de inserção.
  // ---------------------------------------------------------------------------
  const handleHeaderTextChange = useEmojiSafeHandler(onHeaderTextChange);
  const handleFooterTextChange = useEmojiSafeHandler(onFooterTextChange);

  // Ref para o textarea do corpo (usado pelo botão de inserção de emoji)
  const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);

  /**
   * Adiciona um novo botão
   */
  const addButton = () => {
    if (buttons.length >= MAX_BUTTONS) {
      toast.warning(`Máximo de ${MAX_BUTTONS} botões permitidos`);
      return;
    }
    onButtonsChange([...buttons, { type: 'QUICK_REPLY', text: '' }]);
  };

  /**
   * Remove um botão
   */
  const removeButton = (index: number) => {
    onButtonsChange(buttons.filter((_, i) => i !== index));
  };

  // Conjunto de campos de botão que aceitam texto livre e devem ser
  // sanitizados contra emojis. Campos técnicos (phone_number, pix_code,
  // pix_key, amount_value, url, pix_key_type, url_type) ficam de fora.
  const EMOJI_SANITIZED_BUTTON_FIELDS = new Set<keyof TemplateButton>([
    'text',
    'example_value',
    'code_example',
    'merchant_name',
    'item_name',
  ]);

  // Controle de debounce do toast quando emoji é removido via updateButton
  const lastButtonToastAtRef = useRef<number>(0);

  /**
   * Atualiza um campo do botão. Se o campo for de texto livre, remove
   * emojis automaticamente e exibe toast de aviso.
   */
  const updateButton = (index: number, field: keyof TemplateButton, value: string | number) => {
    let nextValue: string | number = value;

    if (typeof value === 'string' && EMOJI_SANITIZED_BUTTON_FIELDS.has(field)) {
      const { clean, removed } = stripEmojis(value);
      nextValue = clean;
      if (removed) {
        const now = Date.now();
        if (now - lastButtonToastAtRef.current > 1500) {
          lastButtonToastAtRef.current = now;
          toast.warning(
            'Emojis não são permitidos em templates do WhatsApp Oficial. O caractere foi removido.'
          );
        }
      }
    }

    const updated = [...buttons];
    updated[index] = { ...updated[index], [field]: nextValue };
    onButtonsChange(updated);
  };

  /**
   * Ícone por tipo de botão
   */
  const getButtonIcon = (type: string) => {
    switch (type) {
      case 'URL':
        return <ExternalLink className="h-4 w-4" />;
      case 'PHONE_NUMBER':
        return <Phone className="h-4 w-4" />;
      case 'COPY_CODE':
        return <Copy className="h-4 w-4" />;
      case 'ORDER_DETAILS':
        return <CreditCard className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-3">
        <Label>Cabeçalho (Opcional)</Label>
        <Select 
          value={headerFormat} 
          onValueChange={(v) => onHeaderFormatChange(v as HeaderFormat)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {HEADER_FORMATS.map(fmt => (
              <SelectItem key={fmt.value} value={fmt.value}>
                {fmt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Campo de texto para header TEXT */}
        {headerFormat === 'TEXT' && (
          <div className="space-y-2">
            <Input
              value={headerText}
              onChange={(e) => handleHeaderTextChange(e.target.value)}
              placeholder="Texto do cabeçalho"
              maxLength={60}
            />
            <p className="text-[10px] text-muted-foreground text-right">
              {headerText.length}/60 caracteres
            </p>
          </div>
        )}

        {/* Seleção de mídia via galeria ou URL manual */}
        {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat) && (
          <div className="space-y-2">
            <MediaUrlInput
              value={headerMediaUrl || null}
              onChange={(url) => onHeaderMediaUrlChange(url || '')}
              placeholder={
                headerFormat === 'IMAGE'
                  ? 'https://exemplo.com/imagem.png'
                  : headerFormat === 'VIDEO'
                  ? 'https://exemplo.com/video.mp4'
                  : 'https://exemplo.com/documento.pdf'
              }
              showPreview={headerFormat === 'IMAGE'}
              allowedTypes={
                headerFormat === 'IMAGE'
                  ? ['image'] as MediaFileType[]
                  : headerFormat === 'VIDEO'
                  ? ['video'] as MediaFileType[]
                  : ['document'] as MediaFileType[]
              }
            />
            <p className="text-[10px] text-muted-foreground">
              Selecione da galeria ou cole uma URL. A mídia será enviada para aprovação da Meta.
            </p>
          </div>
        )}
      </div>

      {/* Body (obrigatório) — permite emojis (Meta aceita no BODY) */}
      <div className="space-y-2">
        <Label>Corpo da Mensagem *</Label>
        <div className="relative">
          <Textarea
            ref={bodyTextareaRef}
            value={bodyText}
            onChange={(e) => onBodyTextChange(e.target.value)}
            placeholder="Digite o corpo da mensagem. Use {{1}}, {{2}} para variáveis."
            rows={5}
            maxLength={1024}
            className="pr-10"
          />
          {/* Botão de inserção de emoji (reaproveita EmojiGrid via Dialog) */}
          <div className="absolute bottom-2 right-2">
            <EmojiInsertButton
              textareaRef={bodyTextareaRef}
              onInsert={onBodyTextChange}
            />
          </div>
        </div>
        <div className="flex justify-between text-[10px] text-muted-foreground">
          <span>Use {`{{1}}`}, {`{{2}}`}, etc. para variáveis dinâmicas</span>
          <span>{bodyText.length}/1024</span>
        </div>

        {/* Campos de exemplo para variáveis do body (exigido pela Meta API) */}
        {uniqueBodyVars.length > 0 && onExampleValueChange && (
          <div className="space-y-2 p-3 rounded-lg border border-border bg-muted/30">
            <Label className="text-xs font-medium text-muted-foreground">
              Valores de exemplo para variáveis (obrigatório para aprovação da Meta)
            </Label>
            {uniqueBodyVars.map(v => {
              const key = v.replace(/[{}]/g, '');
              return (
                <div key={key} className="flex items-center gap-2">
                  <Badge variant="secondary" className="shrink-0 text-xs font-mono">
                    {v}
                  </Badge>
                  <Input
                    value={exampleValues[key] || ''}
                    onChange={(e) => {
                      // Sanitiza emoji nos valores de exemplo (Meta também rejeita)
                      const { clean, removed } = stripEmojis(e.target.value);
                      onExampleValueChange(v, clean);
                      if (removed) {
                        toast.warning(
                          'Emojis não são permitidos em templates do WhatsApp Oficial. O caractere foi removido.'
                        );
                      }
                    }}
                    placeholder={`Ex: valor de exemplo para ${v}`}
                    className="h-8 text-sm"
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="space-y-2">
        <Label>Rodapé (Opcional)</Label>
        <Input
          value={footerText}
          onChange={(e) => handleFooterTextChange(e.target.value)}
          placeholder="Ex: Responda SAIR para cancelar"
          maxLength={60}
        />
        <p className="text-[10px] text-muted-foreground text-right">
          {footerText.length}/60 caracteres
        </p>
      </div>

      {/* Botões */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Botões (Opcional)</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addButton}
            disabled={buttons.length >= MAX_BUTTONS}
          >
            <Plus className="h-4 w-4 mr-1" />
            Adicionar
          </Button>
        </div>

        {buttons.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4 border rounded-lg border-dashed">
            Nenhum botão adicionado
          </p>
        )}

        <div className="space-y-3">
          {buttons.map((button, index) => (
            <div 
              key={index} 
              className="border rounded-lg p-4 space-y-3 bg-muted/30"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getButtonIcon(button.type)}
                  <span className="text-sm font-medium">Botão {index + 1}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => removeButton(index)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Tipo do botão */}
                <div className="space-y-1">
                  <Label className="text-xs">Tipo</Label>
                  <Select
                    value={button.type}
                    onValueChange={(v) => updateButton(index, 'type', v)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BUTTON_TYPES.map(bt => (
                        <SelectItem key={bt.value} value={bt.value}>
                          {bt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Texto do botão */}
                <div className="space-y-1">
                  <Label className="text-xs">Texto</Label>
                  <Input
                    value={button.text}
                    onChange={(e) => updateButton(index, 'text', e.target.value)}
                    placeholder="Texto do botão"
                    maxLength={MAX_BUTTON_TEXT_LENGTH}
                    className="h-9"
                  />
                  <p className="text-[10px] text-muted-foreground text-right">
                    {button.text.length}/{MAX_BUTTON_TEXT_LENGTH}
                  </p>
                </div>
              </div>

              {/* Campos específicos por tipo */}
              {/* Campos URL com seletor Estática/Dinâmica */}
              {button.type === 'URL' && (
                <div className="space-y-2">
                  <Label className="text-xs">URL</Label>
                  <div className="grid grid-cols-[140px_1fr] gap-2">
                    {/* Seletor de tipo de URL: estática ou dinâmica */}
                    <Select
                      value={button.url_type || 'static'}
                      onValueChange={(value) => updateButton(index, 'url_type', value)}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="static">Estática</SelectItem>
                        <SelectItem value="dynamic">Dinâmica</SelectItem>
                      </SelectContent>
                    </Select>
                    {/* Campo de URL com badge {{1}} para dinâmica */}
                    <div className="flex items-center gap-1">
                      <Input
                        value={button.url || ''}
                        onChange={(e) => updateButton(index, 'url', e.target.value)}
                        placeholder={button.url_type === 'dynamic' ? 'https://exemplo.com/' : 'https://exemplo.com'}
                        className="h-9"
                      />
                      {button.url_type === 'dynamic' && (
                        <Badge variant="secondary" className="shrink-0 text-xs">{'{{1}}'}</Badge>
                      )}
                    </div>
                  </div>
                  {/* Campo de exemplo obrigatório para URL dinâmica */}
                  {button.url_type === 'dynamic' && (
                    <div className="ml-[148px] space-y-1">
                      <Label className="text-xs">Exemplo de valor dinâmico</Label>
                      <Input
                        value={button.example_value || ''}
                        onChange={(e) => updateButton(index, 'example_value', e.target.value)}
                        placeholder="ex: abc123"
                        className="h-9"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Valor de exemplo para aprovação da Meta (será substituído dinamicamente)
                      </p>
                    </div>
                  )}
                </div>
              )}

              {button.type === 'PHONE_NUMBER' && (
                <div className="space-y-1">
                  <Label className="text-xs">Telefone</Label>
                  <Input
                    value={button.phone_number || ''}
                    onChange={(e) => updateButton(index, 'phone_number', e.target.value)}
                    placeholder="+55 11 99999-9999"
                    className="h-9"
                  />
                </div>
              )}

              {/* Campos específicos para COPY_CODE (OTP) */}
              {button.type === 'COPY_CODE' && (
                <div className="space-y-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 text-xs font-medium">
                    <Copy className="h-3.5 w-3.5" />
                    Botão para copiar código OTP
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Exemplo de código (para aprovação)</Label>
                    <Input
                      value={button.code_example || ''}
                      onChange={(e) => updateButton(index, 'code_example', e.target.value)}
                      placeholder="Ex: 123456"
                      maxLength={15}
                      className="h-9"
                    />
                    <p className="text-[10px] text-muted-foreground">
                      O código real será enviado dinamicamente
                    </p>
                  </div>
                </div>
              )}

              {/* Campos específicos para ORDER_DETAILS (PIX) */}
              {button.type === 'ORDER_DETAILS' && (
                <div className="space-y-3 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                  <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-xs font-medium">
                    <QrCode className="h-3.5 w-3.5" />
                    Configuração de Pagamento PIX
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Nome do Recebedor *</Label>
                      <Input
                        value={button.merchant_name || ''}
                        onChange={(e) => updateButton(index, 'merchant_name', e.target.value)}
                        placeholder="Nome da empresa"
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Valor (R$) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={button.amount_value || ''}
                        onChange={(e) => updateButton(index, 'amount_value', parseFloat(e.target.value) || 0)}
                        placeholder="0.00"
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Chave PIX *</Label>
                      <Input
                        value={button.pix_key || ''}
                        onChange={(e) => updateButton(index, 'pix_key', e.target.value)}
                        placeholder="CPF, CNPJ, email ou chave"
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Tipo da Chave</Label>
                      <Select
                        value={button.pix_key_type || 'CNPJ'}
                        onValueChange={(v) => updateButton(index, 'pix_key_type', v)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CNPJ">CNPJ</SelectItem>
                          <SelectItem value="CPF">CPF</SelectItem>
                          <SelectItem value="EMAIL">E-mail</SelectItem>
                          <SelectItem value="CELULAR">Celular</SelectItem>
                          <SelectItem value="ALEATORIA">Chave Aleatória</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Código PIX Copia-Cola *</Label>
                    <Input
                      value={button.pix_code || ''}
                      onChange={(e) => updateButton(index, 'pix_code', e.target.value)}
                      placeholder="Cole aqui o código PIX gerado"
                      className="h-9 font-mono text-xs"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Descrição do Item</Label>
                    <Input
                      value={button.item_name || ''}
                      onChange={(e) => updateButton(index, 'item_name', e.target.value)}
                      placeholder="Ex: Pedido #1234"
                      className="h-9"
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
